/**
 * Note detail — terms, investors, the total-note and per-investor schedules, and the WHOLE-note
 * workflow (T3-153): Edit (wizard) / Submit while Draft or Rejected; record the maturity payout
 * batch once matured. Approving or rejecting the note — or its payout — happens on the shared
 * Approvals page (T3-157); this page links to the open queue item.
 *
 * Every action is gated on the user's role permission for MODULES.NOTES; the server enforces the
 * same permission plus the self-approval rule (whoever submitted the note, or recorded its payout,
 * can never decide it), so a disabled button is a convenience, not the control.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, Grid, Chip, Stack, Breadcrumbs, Link, Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Send as SubmitIcon, Check as ApproveIcon,
  Payments as PayoutIcon, FileDownload as ExportIcon,
} from '@mui/icons-material';
import { apiDelete, apiGet, apiPost, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber, formatShortDate, formatDateTime } from '../../utils/formatters';
import { exportToExcel } from '../../utils/excelExport';
import { getStatusColor } from '../../utils/statusColors';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { MODULES } from '../../contexts/PermissionConstants';
import { useNotification } from '../../contexts/NotificationContext';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import NoteLegsTable from '../../components/notes/NoteLegsTable';
import NoteScheduleTable from '../../components/notes/NoteScheduleTable';
import { PayoutDialog } from '../../components/notes/NoteDialogs';
import SubmitForApprovalDialog from '../../components/contracts/SubmitForApprovalDialog';
import DocumentUploadSection from '../../components/documents/DocumentUploadSection';
import { notesToExportRows } from './NoteList';

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const NoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotification();
  const confirm = useConfirmDialog();
  const { hasAction, user } = usePermissions();
  const { isModuleEnabled } = useTenant();
  const enabled = isModuleEnabled(MODULES.NOTES);
  const can = (action) => hasAction(MODULES.NOTES, action);
  const me = (user?.email || '').toLowerCase();

  const [note, setNote] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'payout' | 'submit'

  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setError(null);
    try {
      const [n, sch, s] = await Promise.all([
        apiGet(`/notes/${id}`), apiGet(`/notes/${id}/schedule`), apiGet('/notes/settings'),
      ]);
      setNote(extractApiData(n, null));
      setSchedule(extractApiData(sch, null));
      setSettings(extractApiData(s, null));
    } catch (err) {
      setError(extractApiError(err, 'Could not load the note'));
    } finally {
      setLoading(false);
    }
  }, [id, enabled]);

  useEffect(() => { load(); }, [load]);

  /** Run an action, toast the server's message, reload. Errors toast the API's own message. */
  const run = async (fn, fallback) => {
    setBusy(true);
    try {
      const res = await fn();
      notify.success(res?.message || fallback);
      await load();
    } catch (err) {
      notify.error(extractApiError(err, `${fallback} failed`));
    } finally {
      setBusy(false);
    }
  };

  /** Dialog submit: errors propagate so the dialog stays open and shows them. */
  const save = async (fn, message) => {
    const res = await fn();
    notify.success(res?.message || message);
    await load();
  };

  if (!enabled) return <Alert severity="info">The Notes module is not enabled for this company.</Alert>;
  if (loading) return <LoadingSpinner />;
  if (error || !note) return <Alert severity="error">{error || 'Note not found'}</Alert>;

  const s = note.summary;
  const editable = note.is_editable;
  // Whoever submitted the item (the note, or its payout) cannot decide it.
  const awaitingMine = note.pending_change_type === 'note_payout_approval'
    ? (note.payout_submitted_by || '').toLowerCase() === me
    : (note.submitted_by || '').toLowerCase() === me;
  const anyPricingError = note.legs.some((l) => l.pricing_error);
  const canRecordPayout = note.status === 'Active' && note.is_matured && !note.payout_status && can('update');
  // T3-155: what has been paid against the investors' payment rows (matched in Payment Matching →
  // Notes). The payout is the close-out and needs every investor payment matched first.
  const settled = note.legs.reduce((t, l) => {
    const st = l.settlement || {};
    return {
      investorOwed: t.investorOwed + Number(st.investor_owed || 0),
      investorPaid: t.investorPaid + Number(st.investor_paid || 0),
      whtOwed: t.whtOwed + Number(st.wht_owed || 0),
      whtPaid: t.whtPaid + Number(st.wht_paid || 0),
      openInvestor: t.openInvestor + Number(st.open_investor_rows || 0),
      openWht: t.openWht + Number(st.open_wht_rows || 0),
    };
  }, { investorOwed: 0, investorPaid: 0, whtOwed: 0, whtPaid: 0, openInvestor: 0, openWht: 0 });
  const showSettlement = ['Active', 'Paid'].includes(note.status);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" onClick={() => navigate('/notes')}>Notes</Link>
        <Typography color="text.primary">{note.user_reference}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* T3-158: the user reference leads; the system reference sits beside it. */}
          <Typography variant="h4">{note.user_reference}</Typography>
          <Typography variant="body1" color="text.secondary">{note.reference}</Typography>
          <Chip label={note.status} color={getStatusColor(note.status)} />
          {note.is_matured && note.status === 'Active' && <Chip label="Matured" color="secondary" variant="outlined" />}
          {note.payout_status === 'Pending Approval' && <Chip label="Payout pending approval" color="warning" variant="outlined" />}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button startIcon={<ExportIcon />}
            onClick={() => exportToExcel(notesToExportRows([note]), `${note.user_reference}.csv`, note.user_reference)}>
            Export
          </Button>
          {editable && can('update') && (
            <Button startIcon={<EditIcon />} onClick={() => navigate(`/notes/${note.id}/edit`)}>Edit</Button>
          )}
          {editable && can('delete') && (
            <Button color="error" startIcon={<DeleteIcon />} disabled={busy}
              onClick={() => confirm.show({
                title: 'Delete note',
                message: `Delete ${note.user_reference} (${note.reference}) and its investors? This cannot be undone.`,
                confirmText: 'Delete',
                onConfirm: () => run(async () => { const r = await apiDelete(`/notes/${note.id}`); navigate('/notes'); return r; }, 'Note deleted'),
              })}>
              Delete
            </Button>
          )}
          {editable && can('update') && (
            <Tooltip title={anyPricingError ? 'An investor cannot be priced — edit the note first' : ''}>
              <span>
                <Button variant="contained" startIcon={<SubmitIcon />} disabled={busy || anyPricingError}
                  onClick={() => setDialog('submit')}>
                  {settings?.requireApproval === false ? 'Submit & Activate' : 'Submit for Approval'}
                </Button>
              </span>
            </Tooltip>
          )}
          {/* T3-157: a note (or its payout) awaiting approval is an item on the SHARED approval
              queue — approved or rejected on the Approvals page, like any contract change. */}
          {note.pending_change_id && (
            <Tooltip title={awaitingMine
              ? 'You submitted this, so another user with Notes approval must decide it' : ''}>
              <Button variant="contained" color={awaitingMine ? 'inherit' : 'success'} startIcon={<ApproveIcon />}
                onClick={() => navigate(`/approvals?pendingChangeId=${note.pending_change_id}`)}>
                {note.pending_change_type === 'note_payout_approval' ? 'Review payout in Approvals' : 'Review in Approvals'}
              </Button>
            </Tooltip>
          )}
          {canRecordPayout && (
            <Tooltip title={settled.openInvestor > 0
              ? `${settled.openInvestor} investor payment(s) are not yet matched — match them in Payment Matching → Notes first` : ''}>
              <span>
                <Button variant="contained" startIcon={<PayoutIcon />} disabled={settled.openInvestor > 0}
                  onClick={() => setDialog('payout')}>
                  Record maturity payout
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {note.status === 'Rejected' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rejected{note.rejection_reason ? `: ${note.rejection_reason}` : ''}. Edit the note and submit it again.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Investment type" value={note.investment_type || 'Not set — required to submit'} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Currency" value={note.currency_code} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Gross rate" value={`${Number(note.gross_rate).toFixed(2)}%`} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Start date" value={formatShortDate(note.issue_date)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Maturity" value={formatShortDate(note.maturity_date)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Interest pay dates" value={note.interest_frequency} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Calculation profile" value={note.profile?.name || '-'} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Issuer / programme" value={[note.issuer_name, note.program_name].filter(Boolean).join(' · ') || '-'} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Nominal" value={formatNumber(s.nominal, 2)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Gross interest" value={formatNumber(s.gross_interest, 2)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Commission" value={formatNumber(s.commission_amount, 2)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="WHT" value={formatNumber(s.wht_amount, 2)} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><Stat label="Total due at maturity" value={formatNumber(s.total_due, 2)} /></Grid>
          {showSettlement && (
            <>
              <Grid size={{ xs: 6, md: 2 }}>
                <Stat label="Paid to investors"
                  value={`${formatNumber(settled.investorPaid, 2)} of ${formatNumber(settled.investorOwed, 2)}`} />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <Stat label="WHT remitted"
                  value={`${formatNumber(settled.whtPaid, 2)} of ${formatNumber(settled.whtOwed, 2)}`} />
              </Grid>
            </>
          )}
          {note.submitted_by && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Stat label="Submitted" value={`${note.submitted_by} · ${formatDateTime(note.submitted_at)}`} />
            </Grid>
          )}
          {note.approved_by && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Stat label="Approved" value={`${note.approved_by} · ${formatDateTime(note.approved_at)}`} />
            </Grid>
          )}
          {note.description && <Grid size={{ xs: 12 }}><Stat label="Description" value={note.description} /></Grid>}
        </Grid>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6">Investors</Typography>
          {editable && (
            <Typography variant="body2" color="text.secondary">
              Figures are a preview at the current settings; they are fixed when the note is submitted.
            </Typography>
          )}
        </Box>
        <NoteLegsTable note={note} />
      </Paper>

      {/* T3-161: the note's documents (Investment Agreement, Pricing Supplement, investor
          certificates …), shared document store; required ones are collected on Submit. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <DocumentUploadSection noteId={note.id} allowDelete={false} collapsible={false}
          investors={note.legs.map((l) => ({ investor_id: l.investor_id, investor_name: l.investor_name }))} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Schedule</Typography>
        <NoteScheduleTable schedule={schedule} showSettlement={showSettlement} />
      </Paper>

      {/* Submit = the contract submit-for-approval dialog, for a note: required documents per the
          investment type, uploaded here, then POST /notes/:id/submit {document_ids}. */}
      <SubmitForApprovalDialog open={dialog === 'submit'} noteId={note.id}
        onClose={() => setDialog(null)}
        onSubmitted={async () => { setDialog(null); notify.success('Note submitted'); await load(); }} />
      <PayoutDialog open={dialog === 'payout'} note={note} requireApproval={settings?.requireApproval}
        onClose={() => setDialog(null)}
        onSubmit={(payload) => save(() => apiPost(`/notes/${note.id}/payout`, payload), 'Payout recorded')} />
      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
};

export default NoteDetail;
